import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Sparkles, ArrowRight, CalendarClock, MessageCircle, Wallet,
  Settings2, Share2, HandCoins, Check, LayoutDashboard,
  Scissors, Stethoscope, Brain, PawPrint, Users, Briefcase, HeartPulse,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Footer from "@/components/Footer";
import { resolveBusinessConfig } from "@/config/businessTypes";

import { COMMERCIAL, formatPriceLabel } from "@/config/commercial";

function Section({ id, overline, title, highlight, subtitle, children, centered = false }) {
  return (
    <section
      id={id}
      className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20 animate-fade-up"
    >
      <div className={`max-w-2xl mb-10 md:mb-14 ${centered ? "mx-auto text-center" : ""}`}>
        {overline && <p className="overline mb-3">{overline}</p>}
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05]">
          {title} {highlight && <span className="text-gold italic">{highlight}</span>}
        </h2>
        {subtitle && (
          <p className="mt-4 text-neutral-400 text-base md:text-lg leading-relaxed">{subtitle}</p>
        )}
      </div>
      {children}
    </section>
  );
}

const NICHOS_INLINE =
  "Barberías · Estéticas · Clínicas · Dentistas · Psicólogos · Veterinarias · Coaches · Consultores";

const SECTOR_CARDS = [
  { name: "Barberías", icon: Scissors },
  { name: "Estéticas", icon: Sparkles },
  { name: "Clínicas", icon: HeartPulse },
  { name: "Dentistas", icon: Stethoscope },
  { name: "Psicólogos", icon: Brain },
  { name: "Veterinarias", icon: PawPrint },
  { name: "Coaches", icon: Users },
  { name: "Consultores", icon: Briefcase },
];

const BENEFITS = [
  {
    icon: CalendarClock,
    title: "Agenda inteligente",
    desc: "Organiza horarios y evita dobles reservas.",
    color: "gold",
  },
  {
    icon: Wallet,
    title: "Cobro de anticipos",
    desc: "Solicita un anticipo antes de confirmar la reserva.",
    color: "neonpink",
  },
  {
    icon: MessageCircle,
    title: "Confirmaciones por WhatsApp",
    desc: "Comparte tu enlace y mantén informados a tus clientes.",
    color: "cyan",
  },
  {
    icon: LayoutDashboard,
    title: "Control del negocio",
    desc: "Consulta citas, ingresos y pagos pendientes desde un solo panel.",
    color: "gold",
  },
];

const HOW_IT_WORKS = [
  { icon: Settings2, num: "01", title: "Configura tu agenda", desc: "Define tus servicios, horarios y % de anticipo en menos de 5 minutos." },
  { icon: Share2, num: "02", title: "Comparte tu enlace", desc: "Envía tu página pública a tus clientes por WhatsApp o redes sociales." },
  { icon: HandCoins, num: "03", title: "Cobra el anticipo", desc: "Tu cliente reserva, envía el anticipo y confirma su cita." },
];

const FAQS = [
  {
    q: "¿Cómo cobro el anticipo a mis clientes?",
    a: "Configuras el % de anticipo y tus datos para transferencia bancaria y/o un enlace de Mercado Pago. Tu cliente ve los datos en la página de reserva y te envía el comprobante por WhatsApp.",
  },
  {
    q: "¿Sirve para mi tipo de negocio?",
    a: "Sí. La plataforma está diseñada para cualquier negocio que trabaja por citas: barberías, estéticas, clínicas, dentistas, psicólogos, veterinarias, coaches, consultores y más.",
  },
  {
    q: "¿Necesito instalar una aplicación?",
    a: "No. AgendaPro funciona desde cualquier navegador.",
  },
  {
    q: "¿Puedo usar mi propio banco?",
    a: "Sí. Puedes configurar cualquier banco y también agregar un enlace de Mercado Pago.",
  },
  {
    q: "¿Puedo cambiar de plan?",
    a: "Sí. Puedes cambiar de plan cuando lo necesites.",
  },
];

export default function Landing() {
  const [params] = useSearchParams();
  const businessType = params.get("business");
  const config = useMemo(() => resolveBusinessConfig(businessType), [businessType]);
  const registerHref = businessType ? `/register?business=${businessType}` : "/register";

  return (
    <div className="min-h-screen bg-void text-neutral-100 grain">
      {/* Nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 md:px-10 py-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold to-neonpink flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-void" strokeWidth={2} />
          </div>
          <span className="font-display text-2xl">AgendaPro</span>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm text-neutral-400">
          <a href="#confianza" className="hover:text-gold transition-colors duration-200">Para quién</a>
          <a href="#beneficios" className="hover:text-gold transition-colors duration-200">Beneficios</a>
          <a href="#como-funciona" className="hover:text-gold transition-colors duration-200">Cómo funciona</a>
          <a href="#planes" className="hover:text-gold transition-colors duration-200">Planes</a>
          <a href="#faq" className="hover:text-gold transition-colors duration-200">FAQ</a>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="landing-login-link">
            <Button variant="ghost" className="text-neutral-200 hover:text-gold">Iniciar sesión</Button>
          </Link>
          <Link to={registerHref} data-testid="landing-register-link">
            <Button className="bg-gold text-void hover:bg-gold/90 rounded-full font-bold">Crear cuenta</Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div
          className="absolute inset-0 opacity-40"
          style={{ backgroundImage: `url(${config.heroImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/85 to-void" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-14 pb-24 md:pt-20 md:pb-32 animate-fade-up">
          <p className="overline mb-4" data-testid="landing-overline">
            Agenda · Anticipos · WhatsApp
          </p>
          <h1
            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-3xl"
            data-testid="landing-headline"
          >
            Deja de perder <span className="text-neonpink italic">citas</span>.
            <br />
            Empieza a <span className="text-gold italic">confirmar</span> clientes.
          </h1>
          <p
            className="mt-8 max-w-xl text-neutral-300 text-lg leading-relaxed"
            data-testid="landing-tagline"
          >
            AgendaPro ayuda a cualquier negocio que trabaja por citas a organizar su agenda,
            solicitar anticipos y confirmar reservas desde un solo lugar.
          </p>

          {/* Ideal para */}
          <div className="mt-6 max-w-2xl" data-testid="landing-ideal-para">
            <p className="text-[10px] tracking-[0.28em] uppercase font-bold text-cyan mb-2">
              Ideal para
            </p>
            <p className="text-neutral-400 text-sm md:text-base leading-relaxed">
              {NICHOS_INLINE}
            </p>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to={registerHref} data-testid="cta-register">
              <Button
                size="lg"
                className="bg-gold text-void hover:bg-gold/90 rounded-full font-bold text-base px-8 h-14 transition-transform duration-200 active:scale-95"
              >
                Crear mi agenda gratis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#como-funciona" data-testid="cta-demo">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-cyan/40 text-cyan hover:bg-cyan/10 h-14 px-8 transition-colors duration-200"
              >
                Ver cómo funciona
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Confianza / Sectores */}
      <Section
        id="confianza"
        overline="Para quién es"
        title="Diseñado para negocios que"
        highlight="trabajan por citas."
        subtitle="Cualquier profesional o negocio que agenda con sus clientes puede usar AgendaPro."
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4" data-testid="sectors-grid">
          {SECTOR_CARDS.map((s, i) => (
            <div
              key={s.name}
              data-testid={`sector-${s.name.toLowerCase()}`}
              className="bg-surface gold-border rounded-2xl p-5 md:p-6 flex flex-col items-center text-center gap-3 transition-transform duration-200 hover:-translate-y-1 hover:border-gold/50 animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="w-10 h-10 rounded-full bg-void/60 border border-gold/20 flex items-center justify-center">
                <s.icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
              </div>
              <div className="text-sm font-medium text-neutral-200">{s.name}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Beneficios */}
      <Section
        id="beneficios"
        overline="Por qué AgendaPro"
        title="Todo lo que necesitas,"
        highlight="nada de lo que sobra."
        subtitle="Diseñado para negocios de servicios modernos: rápido, elegante y sin curva de aprendizaje."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="benefits-grid">
          {BENEFITS.map((b, i) => (
            <div
              key={i}
              className="bg-surface gold-border rounded-2xl p-6 transition-transform duration-200 hover:-translate-y-1 hover:border-gold/50 animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`benefit-${i}`}
            >
              <b.icon className={`w-6 h-6 mb-4 text-${b.color}`} strokeWidth={1.5} />
              <h3 className="font-display text-xl mb-2">{b.title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Cómo funciona */}
      <Section
        id="como-funciona"
        overline="Cómo funciona"
        title="Tres pasos,"
        highlight="cero fricción."
        subtitle="Configura tu negocio hoy y empieza a cobrar anticipos esta misma tarde."
      >
        <div className="grid md:grid-cols-3 gap-4">
          {HOW_IT_WORKS.map((s, i) => (
            <div
              key={i}
              className="bg-surface gold-border rounded-2xl p-6 relative transition-transform duration-200 hover:-translate-y-1 hover:border-gold/50 animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`step-${i}`}
            >
              <span className="absolute top-4 right-6 font-display text-6xl text-gold/10 leading-none">{s.num}</span>
              <s.icon className="w-6 h-6 mb-4 text-gold" strokeWidth={1.5} />
              <h3 className="font-display text-xl mb-2">{s.title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Capturas (mockup) */}
      <Section
        id="capturas"
        overline="Vista rápida"
        title="Un panel que se ve tan bien"
        highlight="como tu marca."
        subtitle="Diseño premium, modo oscuro por defecto, mobile-first."
      >
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-surface gold-border rounded-2xl p-6 relative overflow-hidden transition-transform duration-200 hover:-translate-y-1" data-testid="capture-dashboard">
            <p className="overline mb-3">Dashboard</p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[{ l: "Citas hoy", v: "5", c: "#FFD700" }, { l: "Cobrado", v: "$1,240", c: "#00E5FF" }, { l: "Pendiente", v: "$540", c: "#FF3AD2" }].map((s, i) => (
                <div key={i} className="bg-void/60 border border-gold/10 rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{s.l}</div>
                  <div className="font-display text-2xl" style={{ color: s.c }}>{s.v}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {[{ t: "10:00", n: "María L.", s: "Servicio 1", st: "Pagada" }, { t: "11:30", n: "Ana T.", s: "Servicio 2", st: "Confirmada" }, { t: "13:00", n: "Sofía R.", s: "Servicio 3", st: "Pendiente" }].map((a, i) => (
                <div key={i} className="flex items-center gap-3 bg-void/60 border border-gold/10 rounded-lg px-3 py-2">
                  <span className="font-mono text-cyan text-sm w-14">{a.t}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{a.n}</div>
                    <div className="text-[10px] text-neutral-500 truncate">{a.s}</div>
                  </div>
                  <span className="text-[9px] uppercase tracking-widest text-gold">{a.st}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-surface gold-border rounded-2xl p-6 flex flex-col justify-between transition-transform duration-200 hover:-translate-y-1" data-testid="capture-booking">
            <div>
              <p className="overline mb-3">Reserva pública</p>
              <div className="space-y-2">
                {(config.defaultServices || []).slice(0, 3).map((s, i) => (
                  <div key={i} className="flex items-center justify-between bg-void/60 border border-gold/10 rounded-lg px-3 py-3">
                    <div className="text-sm">{s.name}</div>
                    <div className="text-gold font-display text-lg">${s.price}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 text-center">
              <div className="inline-block bg-gold text-void text-xs font-bold px-4 py-2 rounded-full">
                Confirmar por WhatsApp
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Planes — Single PRO plan */}
      <Section
        id="planes"
        overline="Plan único"
        title="Un solo plan."
        highlight="Todo lo que tu negocio necesita."
        subtitle="Sin niveles. Sin restricciones. Empieza gratis."
        centered
      >
        <div className="max-w-md mx-auto" data-testid="plans-grid">
          <div
            data-testid="plan-pro"
            className="bg-surface rounded-3xl p-8 md:p-10 border-2 border-gold relative overflow-hidden transition-transform duration-200 hover:-translate-y-1 animate-fade-up"
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gold/10 blur-3xl" />
            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <p className="overline text-gold">{COMMERCIAL.planName}</p>
                <span
                  data-testid="trial-badge"
                  className="text-[10px] uppercase tracking-widest text-void bg-gold px-3 py-1.5 rounded-full font-bold"
                >
                  {COMMERCIAL.trialDays} días GRATIS
                </span>
              </div>
              <div className="flex items-baseline gap-1 mb-3" data-testid="plan-price">
                <span className="font-display text-6xl">{COMMERCIAL.currencySymbol}{COMMERCIAL.price}</span>
                <span className="text-neutral-500 text-sm">{COMMERCIAL.currency} / {COMMERCIAL.billingCycle}</span>
              </div>
              <p className="text-neutral-200 text-sm leading-relaxed" data-testid="plan-trial-line">
                Empieza hoy con {COMMERCIAL.trialDays} días de prueba gratuita.
              </p>
              <p className="text-neutral-500 text-xs leading-relaxed mb-8 mt-1" data-testid="plan-trial-sub">
                Acceso completo durante todo el periodo de prueba.
              </p>
              <ul className="space-y-2.5 mb-8">
                {COMMERCIAL.features.map((f, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-neutral-200" data-testid={`plan-feature-${k}`}>
                    <Check className="w-4 h-4 text-cyan mt-0.5 shrink-0" strokeWidth={2} /> {f}
                  </li>
                ))}
              </ul>
              <Link to={registerHref}>
                <Button
                  className="w-full bg-gold text-void hover:bg-gold/90 rounded-full font-bold h-12 transition-transform duration-200 active:scale-95"
                  data-testid="plan-cta-btn"
                >
                  Crear mi cuenta gratis
                </Button>
              </Link>
              <p className="text-[12px] text-center text-neutral-300 mt-3 flex items-center justify-center gap-1.5" data-testid="plan-trust-line">
                <Check className="w-3.5 h-3.5 text-cyan" strokeWidth={2.5} />
                Acceso completo durante tu periodo de prueba.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section
        id="faq"
        overline="Preguntas frecuentes"
        title="Antes de que"
        highlight="preguntes."
        subtitle="Las dudas más comunes de quienes ya usan la plataforma."
      >
        <div className="bg-surface gold-border rounded-2xl p-2 md:p-4">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-gold/10" data-testid={`faq-${i}`}>
                <AccordionTrigger className="px-4 md:px-6 text-left hover:text-gold hover:no-underline font-medium transition-colors duration-200">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="px-4 md:px-6 text-neutral-400 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* Final CTA */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="landing-cta">
        <div className="bg-gradient-to-br from-surface via-surface to-void gold-border rounded-3xl p-8 md:p-14 text-center relative overflow-hidden animate-fade-up">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-neonpink/10 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-cyan/10 blur-3xl" />
          <p className="overline mb-4 relative">Empieza hoy</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl mx-auto relative">
            Empieza <span className="text-gold italic">hoy.</span>
          </h2>
          <p className="mt-6 text-neutral-400 max-w-xl mx-auto relative">
            Organiza tu agenda, reduce cancelaciones y dedica más tiempo a atender a tus clientes.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center relative">
            <Link to={registerHref} data-testid="cta-final-register">
              <Button
                size="lg"
                className="bg-gold text-void hover:bg-gold/90 rounded-full font-bold text-base px-8 h-14 transition-transform duration-200 active:scale-95"
              >
                Crear mi agenda gratis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login" data-testid="cta-final-demo">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-cyan/40 text-cyan hover:bg-cyan/10 h-14 px-8 transition-colors duration-200"
              >
                Ver demo en vivo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
